import { lazy, useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { Navigate, Route, Routes } from 'react-router-dom';
import DashboardLayout from '@senderra/shared/layouts/dashboard';
import { withSuspense } from '@senderra/shared/hocs';
import { useAutoLogout } from '@senderra/shared/hooks/useAuth0AutoLogout';
import { RootState } from '../store';
import { useUserRole } from '@senderra/shared/features/user-management/hooks/useRoleProfile';
import PageLoader from '@senderra/shared/components/page-loader';
import { getPagesByRole, getRoutesByRole } from './pages/utils';
import { useAuth0 } from '@auth0/auth0-react';
import { useGetEConsentStatusQuery } from '@senderra/shared/features/e-consent/api/eConsentPharmaNavigatorApi';
import InactivityScreen from '@senderra/shared/components/inactivity-screen';
import ErrorScreen from '@senderra/shared/components/error-screen';

import { useTrackPageActivity } from '@senderra/shared/hooks/useTrackPageActivity';
import { useUserTheme } from '@senderra/shared/features/theme/hooks';
import { RoleName } from '@senderra/shared/types';
import { SubdomainStatus } from '@senderra/shared/features/auth/slices/authSlice';

const EConsentValidatePage = withSuspense(lazy(() => import('./pages/e-consent-validate')));

export function App() {
    const { isAuthenticated, isLoading: auth0Loading } = useAuth0();
    const { isLoggedIn, subdomainValid, loginActivityComplete, authError } = useSelector(
        (state: RootState) => state.auth
    );
    const { isLoading: eConsentLoading } = useGetEConsentStatusQuery(undefined, {
        skip: !isAuthenticated || !isLoggedIn || !loginActivityComplete,
    });
    const { isConsentAccepted } = useSelector((state: RootState) => state.eConsentPlatform);
    const { inactivityReason, showLoginButton } = useSelector((state: RootState) => state.inactivityScreen);
    const { isLoading: themeLoading } = useUserTheme();
    const [logoutPending, setLogoutPending] = useState(false);

    const { permissions, isLoading: userRoleLoading } = useUserRole();
    const navPages = useMemo(() => getPagesByRole(permissions), [permissions]);
    const routes = useMemo(() => getRoutesByRole(permissions), [permissions]);

    const navigationRoute = isAuthenticated
        ? isConsentAccepted || inactivityReason
            ? '/'
            : '/e-consent-validate'
        : '/login';

    const { userLogout } = useAutoLogout();

    useEffect(() => {
        if (isAuthenticated) {
            const currentSubdomain = window.location.hostname.split('.')[0];
            const targetSubdomain = localStorage.getItem('loginRedirect');
            if (targetSubdomain && currentSubdomain !== targetSubdomain) {
                const newOrigin = window.location.origin.replace(/^[^.]+\./, '');
                const redirectTo = `https://${newOrigin}`;
                window.location.href = redirectTo;
            }
        }
    }, [isAuthenticated]);

    useEffect(() => {
        if (
            !logoutPending &&
            !userRoleLoading &&
            !(permissions.includes(RoleName.TENANT_ADMIN) || permissions.includes(RoleName.USER)) &&
            !inactivityReason
        ) {
            setLogoutPending(true);
            userLogout();
        }
    }, [logoutPending, permissions, userLogout, userRoleLoading]);

    useTrackPageActivity();

    if (subdomainValid === SubdomainStatus.INVALID) {
        return (
            <Routes>
                <Route path='*' element={<ErrorScreen />} />
            </Routes>
        );
    }

    if (
        logoutPending ||
        auth0Loading ||
        eConsentLoading ||
        userRoleLoading ||
        themeLoading ||
        (!loginActivityComplete && !authError) ||
        (!inactivityReason && !isLoggedIn)
    ) {
        return <PageLoader />;
    }

    return (
        <>
            {inactivityReason ? (
                <InactivityScreen message={inactivityReason} showLoginButton={showLoginButton} />
            ) : (
                <Routes>
                    {isAuthenticated && isConsentAccepted ? (
                        routes.map(({ url, component: Component }) => (
                            <Route
                                key={url}
                                path={url}
                                element={
                                    <DashboardLayout navPages={navPages} includeLogo>
                                        <Component />
                                    </DashboardLayout>
                                }
                            />
                        ))
                    ) : (
                        <Route path='/e-consent-validate' element={<EConsentValidatePage />} />
                    )}
                    <Route path='*' element={<Navigate to={navigationRoute} replace />} />
                </Routes>
            )}
        </>
    );
}

export default App;
